/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { ScatterChart } from "../../models/http/charts/scatter-chart/scatter-chart.model";
import { AdvancedFilters } from "../../models/http/dashboard/advanced-filters.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardSciHttpService {
	private _dashboardSciOperations = ENDPOINT_GROUPS.dashboardSci.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves the green score data for a scatter chart within a specified date range and for a specific tenant and functional unit.
	 *
	 * @param filters - The AdvancedFilters payload as defined in the cursor-based filters API spec.
	 * @returns An Observable that emits the scatter chart data.
	 */
	public getGreenScore(filters: AdvancedFilters): Observable<ChartResponse<ScatterChart>> {
		return this._communicationService
			.callService(
				this._dashboardSciOperations.getGreenScore.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as ScatterChart, hasError: true }))
			);
	}

	/**
	 * Retrieves 10 worst emissions by SCI for a bar chart.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the bar chart data.
	 */
	public getWorst10Emissions(filters: AdvancedFilters): Observable<ChartResponse<BarChart>> {
		return this._communicationService
			.callService(
				this._dashboardSciOperations.getWorst10Emissions.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}
}
